import clsx from "clsx";
import { forwardRef } from "react";
import { useState } from "react";
import { FieldError, UseFormRegister } from "react-hook-form";

import { Hide, Show } from "@/assets/icons/icons";

type Styles = {
  container?: string;
  error?: string;
  inputBase?: string;
  inputDisabled: string;
  inputError: string;
  hint?: string;
  label?: string;
};

type Props = {
  customStyles?: Styles;
  disabled?: boolean;
  error?: FieldError | undefined;
  hint?: string;
  id: string;
  inputType?: "text" | "password" | "email";
  labelText: string;
  name?: string;
  placeholder?: string;
  register?: ReturnType<UseFormRegister<any>>;
  required?: boolean;
  [x: string]: any;
};

export const TextField = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    customStyles,
    disabled,
    error,
    hint,
    id,
    inputType = "text",
    labelText,
    name,
    placeholder,
    required,
    onChange,
    onBlur,
    onFocus,
    register,
    ...rest
  } = props;

  // Add state for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = inputType === "password";

  return (
    <div className={clsx("relative mb-4 max-w-full", customStyles?.container)}>
      <label
        htmlFor={id}
        className={clsx(
          "mb-2 block text-lg font-semibold text-white",
          customStyles?.label,
        )}
      >
        {labelText}
      </label>

      <div className="relative">
        <input
          className={clsx(
            "max-w-full !bg-white py-3 pl-4 pr-10 text-base text-black", // pr-10 for icon space
            disabled && "cursor-not-allowed bg-gray-100",
            disabled && customStyles?.inputDisabled,
            error && "border-red-500 focus:border-red-500 focus:ring-red-500",
            error && customStyles?.inputError,
          )}
          id={id}
          disabled={disabled}
          placeholder={placeholder}
          type={isPassword && showPassword ? "text" : inputType}
          name={name}
          ref={ref}
          onBlur={onBlur}
          onFocus={onFocus}
          {...(register || {})}
          {...rest}
        />

        {/* Show/hide password icon */}
        {isPassword && (
          <button
            type="button"
            className="absolute inset-y-0 right-3 flex items-center p-1 focus:outline-none"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <Hide width={30} height={30} />
            ) : (
              <Show width={30} height={30} />
            )}
          </button>
        )}
      </div>

      <div className="h-4">
        {error?.message && (
          <p className={clsx("text-xs text-error-100", customStyles?.error)}>
            {error.message}
          </p>
        )}
      </div>

      {hint && (
        <p className={clsx("text-xs text-white", customStyles?.hint)}>{hint}</p>
      )}
    </div>
  );
});

TextField.displayName = "TextField";
